interface SessionCacheOptions {
  windowKey: string
  sessionPrefix: string
}

/**
 * Two-layer loading strategy:
 *
 * 1. sessionStorage – persists data across app navigations (www → BaB → journal)
 *    so coupon/flag data loads instantly without a flash-in effect.
 *
 * 2. inflight request deduping – within a single page load, multiple components
 *    (lyka-nav, lyka-coupon-banner, page script) may load flagsmith/coupons at the same time.
 *    This ensures only one request is made.
 */
export function createSessionCache<T>({ windowKey, sessionPrefix }: SessionCacheOptions) {
  function sessionKey(key: string): string {
    return `${sessionPrefix}:${key}`
  }

  function readSession(key: string): T | undefined {
    if (typeof sessionStorage === 'undefined') return undefined

    const raw = sessionStorage.getItem(sessionKey(key))
    if (!raw) return undefined

    try {
      const parsed = JSON.parse(raw)
      return parsed as T
    } catch {
      sessionStorage.removeItem(sessionKey(key))
      return undefined
    }
  }

  function writeSession(key: string, value: T): void {
    if (typeof sessionStorage === 'undefined') return
    sessionStorage.setItem(sessionKey(key), JSON.stringify(value))
  }

  function getInflightRequests(): Map<string, Promise<T>> {
    const w = window as unknown as Record<string, unknown>

    if (!(w[windowKey] instanceof Map)) {
      w[windowKey] = new Map<string, Promise<T>>()
    }

    return w[windowKey] as Map<string, Promise<T>>
  }

  return async function load(key: string, fetcher: () => Promise<T>): Promise<T> {
    const sessionValue = readSession(key)
    if (sessionValue !== undefined) return sessionValue

    const inflight = getInflightRequests()
    const pending = inflight.get(key)
    if (pending) return pending

    const request = fetcher()
      .then((value) => {
        writeSession(key, value)
        return value
      })
      .finally(() => {
        inflight.delete(key)
      })

    inflight.set(key, request)
    return request
  }
}
